"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import Image, { StaticImageData } from "next/image";
import { Images, Icons } from "@/app/assets/shared";
import PrimaryBtn from "@/components/PrimaryBtn";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { CLIENT_ENTRIES, ClientEntry } from "./data";
import { ensureGsap } from "@/lib/EnsureGsap";

const SCROLL_PER_CLIENT_SVH = 60;
/** Height of one logo row in the vertical rail (px). */
const RAIL_ITEM_H = 130;
const RAIL_EASE = 0.1;
const TICK_INACTIVE_W = 12;
const TICK_ACTIVE_W = 40;
const TICK_INACTIVE_H = 1;
const TICK_ACTIVE_H = 2;
const DOT_PERIOD = 16;
/** Start scroll tracking / warm GSAP this far before the section is visible. */
const NEAR_VIEWPORT_MARGIN = "100% 0px 100% 0px";

const REVEAL_ITEM_CLASS = "case-study-reveal-item";

type SectionKey = "challenge" | "solution" | "impact";
type IconSrc = string | StaticImageData | undefined;

/* ---------- section icons (images) ---------- */
const SECTION_META: Record<SectionKey, { label: string; icon: IconSrc }> = {
  challenge: {
    label: "The Challenge",
    icon: Icons?.UpdatedServicesIcon?.showcaseIcon1,
  },
  solution: {
    label: "The Solution",
    icon: Icons?.UpdatedServicesIcon?.showcaseIcon2,
  },
  impact: {
    label: "The Impact",
    icon: Icons?.UpdatedServicesIcon?.showcaseIcon3,
  },
};

const CHECK_ICON: IconSrc = undefined;

const SectionIcon = ({ src }: { src: IconSrc }) =>
  src ? (
    <Image
      src={src}
      alt=""
      aria-hidden
      width={28}
      height={28}
      className="h-7 w-7 object-contain"
    />
  ) : null;

const CheckIcon = () =>
  CHECK_ICON ? (
    <Image
      src={CHECK_ICON}
      alt=""
      aria-hidden
      width={20}
      height={20}
      className="mt-[4px] h-5 w-5 shrink-0"
    />
  ) : (
    <svg
      className="mt-[4px] h-5 w-5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle cx="12" cy="12" r="12" fill="#225BD4" />
      <path
        d="m7 12.5 3.2 3.2L17 9"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

export default function SuccessStoryShowcase({
  clients = CLIENT_ENTRIES,
}: {
  clients?: ClientEntry[];
}) {
  const isMobile = useIsMobile();
  const total = clients.length;

  const [activeIndex, setActiveIndex] = useState(0);
  const [openSection, setOpenSection] = useState<SectionKey | null>(
    "challenge",
  );

  const trackRef = useRef<HTMLDivElement | null>(null);
  const contentBoxRef = useRef<HTMLDivElement | null>(null);
  const mobileRailRef = useRef<HTMLDivElement | null>(null);
  const railInnerRef = useRef<HTMLDivElement | null>(null);
  const railDotsRef = useRef<HTMLDivElement | null>(null);
  const railItemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const railTickRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const mobileBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const prevRevealIndexRef = useRef(0);

  const activeClient = clients[activeIndex] ?? clients[0];
  const caseStudy = activeClient?.caseStudy;

  /* ---------- warm up GSAP only once the section is near the viewport ---------- */
  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          ensureGsap().catch(() => {});
          io.disconnect();
        }
      },
      { rootMargin: NEAR_VIEWPORT_MARGIN },
    );
    io.observe(track);
    return () => io.disconnect();
  }, []);

  /* ---------- scroll -> continuous rail position + active index ----------
     The scroll listener is only attached while the section is near the
     viewport, so the rest of the page scrolls with zero overhead from here. */
  useEffect(() => {
    // Mobile: no scroll-driven tabs — the user taps the one they want.
    const track = trackRef.current;
    if (total < 1 || isMobile || !track) return;

    let raf = 0;
    let target = 0;
    let current = 0;
    let scrollable = 0; // cached; only changes on resize
    let lastIndex = -1;
    let listening = false;

    const measure = () => {
      scrollable = track.offsetHeight - window.innerHeight;
    };

    // Moves the dotted line + logos to a (fractional) position, no re-render.
    // Only transform / opacity are written, so no layout work per frame.
    const applyRail = (pos: number) => {
      const offset = pos * RAIL_ITEM_H;
      if (railInnerRef.current) {
        railInnerRef.current.style.transform = `translate3d(0, ${-offset}px, 0)`;
      }
      if (railDotsRef.current) {
        railDotsRef.current.style.transform = `translate3d(0, ${-(offset % DOT_PERIOD)}px, 0)`;
      }
      railItemRefs.current.forEach((el, i) => {
        if (!el) return;
        const d = i - pos;
        const opacity = d < 0 ? Math.max(0, 1 + d) : Math.max(0.2, 1 - d * 0.5);
        el.style.opacity = String(opacity);

        // Indicator line: long + bright when active, short + dim when not.
        const tick = railTickRefs.current[i];
        if (tick) {
          const t = Math.max(0, 1 - Math.abs(d)); // 1 = active, 0 = inactive
          const w = TICK_INACTIVE_W + (TICK_ACTIVE_W - TICK_INACTIVE_W) * t;
          const h = TICK_INACTIVE_H + (TICK_ACTIVE_H - TICK_INACTIVE_H) * t;
          tick.style.transform = `scale(${w / TICK_ACTIVE_W}, ${h / TICK_ACTIVE_H})`;
          tick.style.opacity = String(0.45 + 0.55 * t);
        }
      });
    };

    const commitIndex = (pos: number) => {
      const idx = Math.round(pos);
      if (idx !== lastIndex) {
        lastIndex = idx;
        setActiveIndex(idx);
      }
    };

    const readTarget = () => {
      if (scrollable <= 0) return;
      const rect = track.getBoundingClientRect();
      const progress = Math.min(Math.max(-rect.top / scrollable, 0), 1);
      target = Math.min(Math.max(progress * total - 0.5, 0), total - 1);
    };

    const tick = () => {
      raf = 0;
      const diff = target - current;
      current = Math.abs(diff) < 0.001 ? target : current + diff * RAIL_EASE;
      applyRail(current);
      commitIndex(current);
      if (current !== target) raf = requestAnimationFrame(tick);
    };

    const onScroll = () => {
      readTarget();
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const onResize = () => {
      measure();
      onScroll();
    };

    const startListening = () => {
      if (listening) return;
      listening = true;
      measure();
      onScroll();
      window.addEventListener("scroll", onScroll, { passive: true });
    };

    const stopListening = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener("scroll", onScroll);
      // Let the rail finish easing to wherever the user left it.
      readTarget();
      if (!raf) raf = requestAnimationFrame(tick);
    };

    // Initial sync (handles reloads mid-section).
    measure();
    readTarget();
    current = target;
    applyRail(current);
    commitIndex(current);

    window.addEventListener("resize", onResize);

    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => {
          const last = entries[entries.length - 1];
          if (last.isIntersecting) startListening();
          else stopListening();
        },
        { rootMargin: NEAR_VIEWPORT_MARGIN },
      );
      io.observe(track);
    } else {
      startListening();
    }

    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [total, isMobile]);

  /* ---------- click logo / dot -> scroll to that client ---------- */
  const scrollToIndex = useCallback(
    (index: number) => {
      if (isMobile) {
        setActiveIndex(index);
        return;
      }
      const track = trackRef.current;
      if (!track) return;
      const scrollable = track.offsetHeight - window.innerHeight;
      const trackTop = window.scrollY + track.getBoundingClientRect().top;
      window.scrollTo({
        top: trackTop + ((index + 0.5) / total) * scrollable,
        behavior: "smooth",
      });
    },
    [total, isMobile],
  );

  /* ---------- reset accordion when the client changes ---------- */
  useEffect(() => {
    setOpenSection("challenge");
  }, [activeIndex]);

  /* ---------- keep the mobile logo strip in view (mobile only) ---------- */
  useEffect(() => {
    if (!isMobile) return;
    const rail = mobileRailRef.current;
    const btn = mobileBtnRefs.current[activeIndex];
    if (rail && btn) {
      rail.scrollTo({ left: btn.offsetLeft - 16, behavior: "smooth" });
    }
  }, [activeIndex, isMobile]);

  /* ---------- content reveal on change ----------
     Skipped on first mount: the content is already server-rendered and
     visible, so animating it would only cause a flash before anyone sees it. */
  useEffect(() => {
    if (prevRevealIndexRef.current === activeIndex) return;
    prevRevealIndexRef.current = activeIndex;

    let cancelled = false;
    ensureGsap().then(({ gsap }) => {
      if (cancelled) return;
      const items = contentBoxRef.current?.querySelectorAll(
        `.${REVEAL_ITEM_CLASS}`,
      );
      if (items?.length) {
        gsap.fromTo(
          items,
          { opacity: 0, y: 16 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power3.out",
            stagger: 0.07,
            overwrite: true,
            clearProps: "transform",
          },
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeIndex]);

  const toggleSection = (key: SectionKey) =>
    setOpenSection((current) => (current === key ? null : key));

  /* ---------- Challenge / Solution / Impact block ---------- */
  const renderSection = (key: SectionKey, body: React.ReactNode) => {
    // Accordion state only matters on mobile; desktop always shows the body via CSS.
    const isOpen = openSection === key;
    const { label, icon } = SECTION_META[key];
    const iconEl = <SectionIcon src={icon} />;

    return (
      <div className={`${REVEAL_ITEM_CLASS} flex items-start gap-4 lg:gap-5`}>
        <div className="hidden h-[52px] w-[52px] shrink-0 items-center justify-center bg-white shadow-[0_1px_8px_rgba(34,91,212,0.14)] lg:flex">
          {iconEl}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <button
            type="button"
            onClick={() => isMobile && toggleSection(key)}
            aria-expanded={isMobile ? isOpen : true}
            className="flex w-full items-center justify-between gap-2 text-left lg:pointer-events-none"
          >
            <span className="flex items-center gap-2">
              <span className="lg:hidden">{iconEl}</span>
              <span className="text-[18px] font-medium text-[#0a0a0a] lg:text-[22px]">
                {label}
              </span>
            </span>
            <svg
              className={`h-4 w-4 shrink-0 text-[#0a0a0a] transition-transform duration-200 lg:hidden ${
                isOpen ? "rotate-180" : ""
              }`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          <div
            className={`${
              isOpen ? "block" : "hidden"
            } max-w-[60ch] text-[16px] leading-[150%] text-[#4a4a4a] lg:block lg:text-[17px] lg:leading-[150%]`}
          >
            {body}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="success-story-showcase relative w-full overflow-clip bg-black">
      {/* Backgrounds */}
      <div
        className="texture-bg pointer-events-none absolute inset-0 z-0 lg:mix-blend-overlay"
        aria-hidden
      >
        {/* Desktop only; display:none on mobile means the lazy image is never fetched */}
        <Image
          src={Images?.texture}
          alt=""
          fill
          className="hidden object-cover opacity-60 mix-blend-overlay lg:block"
          sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
        />
      </div>
      {/* Mobile: cheap gradient glow (huge CSS blurs freeze phones) */}
      <div
        className="pointer-events-none absolute inset-0 z-0 lg:hidden"
        style={{
          backgroundImage:
            "radial-gradient(circle at 100% 0%, rgba(34,91,212,0.35), transparent 55%), radial-gradient(circle at 0% 100%, rgba(34,91,212,0.35), transparent 55%)",
        }}
      />
      {/* Desktop: glow blobs — radial gradients instead of 333px CSS blurs
          (same look, a fraction of the paint cost). */}
      <div
        className="pointer-events-none absolute -right-[649px] -top-[649px] hidden h-[1600px] w-[1600px] lg:block"
        style={{
          backgroundImage:
            "radial-gradient(closest-side, rgba(34,91,212,0.26) 0%, rgba(34,91,212,0.14) 45%, rgba(34,91,212,0.04) 80%, transparent 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute -bottom-[649px] -left-[649px] hidden h-[1600px] w-[1600px] lg:block"
        style={{
          backgroundImage:
            "radial-gradient(closest-side, rgba(34,91,212,0.26) 0%, rgba(34,91,212,0.14) 45%, rgba(34,91,212,0.04) 80%, transparent 100%)",
        }}
      />

      {/* Header — left aligned, scrolls normally (NOT pinned) */}
      <div className="relative z-[1] mx-auto flex w-[calc(100%_-_10%)] flex-col gap-3 pb-8 pt-[50px] text-left lg:flex-row lg:items-end lg:justify-between lg:gap-10 lg:pb-12 lg:pt-[100px]">
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium uppercase tracking-tight text-white">
            Our Success Stories
          </p>
          <h2 className="clash-font max-w-[24ch] text-[length:var(--section-title)] font-medium leading-[110%]">
            <span className="text-white">Your Idea, Our Expertise:</span>
            <br />
            {/* <span className="text-[#225BD4]">Real Stories </span> */}
            <span className="text-[#225BD4]">Our Success Stories</span>
          </h2>
        </div>
        <p className="max-w-[46ch] pb-2 text-[length:var(--hero-para)] font-normal leading-[150%] text-white/50">
          Our work speaks louder than words. We collaborate with visionary
          brands to solve complex challenges and deliver measurable outcomes.
        </p>
      </div>

      {/* Tall track: gives the page room to scroll while rail + box are pinned.
          Height / sticky are CSS-breakpoint driven (not JS) so the first paint
          is already correct on both mobile and desktop — no layout shift. */}
      <div
        ref={trackRef}
        className="relative z-[1] lg:h-[var(--track-h)]"
        style={
          {
            "--track-h": `calc(100svh + ${total * SCROLL_PER_CLIENT_SVH}svh)`,
          } as React.CSSProperties
        }
      >
        {/* Pinned stage: rail + box + dots only */}
        <div className="flex w-full items-center pb-10 lg:sticky lg:top-0 lg:h-[100svh] lg:pb-0">
          <div className="mx-auto flex w-[calc(100%_-_10%)] flex-col gap-4 py-4 lg:h-full lg:flex-row lg:gap-10 lg:py-8 lg:pb-22">
            {/* Mobile: horizontal logo strip */}
            <div
              ref={mobileRailRef}
              className="no-scrollbar -mx-1 flex shrink-0 gap-2 overflow-x-auto px-1 lg:hidden"
            >
              {clients.map((client, i) => {
                const isActive = i === activeIndex;
                return (
                  <button
                    key={client.id}
                    ref={(el) => {
                      mobileBtnRefs.current[i] = el;
                    }}
                    type="button"
                    onClick={() => scrollToIndex(i)}
                    aria-pressed={isActive}
                    aria-label={client.name}
                    className={`flex h-[52px] shrink-0 items-center justify-center rounded-full px-6 transition-colors ${
                      isActive
                        ? "bg-[#225BD4]"
                        : "border border-white/20 bg-white/5"
                    }`}
                  >
                    {client.logo ? (
                      <Image
                        src={client.logo}
                        alt={client.name}
                        width={140}
                        height={32}
                        className={`h-[28px] w-auto object-contain ${
                          isActive ? "opacity-100" : "opacity-50"
                        }`}
                      />
                    ) : (
                      <span
                        className={`text-sm font-medium ${
                          isActive ? "text-white" : "text-white/50"
                        }`}
                      >
                        {client.name}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Desktop: dotted rail — dots + logos move continuously with scroll;
                each logo's indicator line lights up when it becomes active */}
            <div
              className="relative hidden shrink-0 overflow-hidden lg:block lg:w-[240px]"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent 0, #000 8%, #000 75%, transparent 100%)",
                maskImage:
                  "linear-gradient(to bottom, transparent 0, #000 8%, #000 75%, transparent 100%)",
              }}
            >
              {/* Dotted line — moved with transform (compositor) instead of
                  background-position (repaint). Extra height hides the seam. */}
              <div
                ref={railDotsRef}
                className="absolute left-0 top-0 w-[6px] will-change-transform"
                style={{
                  height: `calc(100% + ${DOT_PERIOD}px)`,
                  backgroundImage:
                    "radial-gradient(circle at 3px 8px, rgba(255,255,255,0.55) 1.6px, transparent 2px)",
                  backgroundSize: `6px ${DOT_PERIOD}px`,
                  backgroundRepeat: "repeat-y",
                }}
              />
              <div ref={railInnerRef} className="will-change-transform">
                {clients.map((client, i) => (
                  <button
                    key={client.id}
                    ref={(el) => {
                      railItemRefs.current[i] = el;
                    }}
                    type="button"
                    onClick={() => scrollToIndex(i)}
                    aria-pressed={i === activeIndex}
                    aria-label={client.name}
                    className="relative flex w-full items-center pl-[64px] text-left"
                    style={{
                      height: RAIL_ITEM_H,
                      opacity: i === 0 ? 1 : Math.max(0.2, 1 - i * 0.5),
                    }}
                  >
                    {/* Indicator line for this logo (active <-> inactive).
                        Fixed active size, scaled with transform so no layout. */}
                    <span
                      ref={(el) => {
                        railTickRefs.current[i] = el;
                      }}
                      aria-hidden
                      className="absolute left-[3px] bg-white"
                      style={{
                        top: "50%",
                        marginTop: -TICK_ACTIVE_H / 2,
                        width: TICK_ACTIVE_W,
                        height: TICK_ACTIVE_H,
                        transformOrigin: "left center",
                        transform:
                          i === 0
                            ? "scale(1, 1)"
                            : `scale(${TICK_INACTIVE_W / TICK_ACTIVE_W}, ${
                                TICK_INACTIVE_H / TICK_ACTIVE_H
                              })`,
                        opacity: i === 0 ? 1 : 0.45,
                      }}
                    />
                    {client.logo ? (
                      <Image
                        src={client.logo}
                        alt={client.name}
                        width={180}
                        height={48}
                        className="h-[44px] w-auto max-w-[150px] object-contain"
                      />
                    ) : (
                      <span className="text-lg font-medium text-white">
                        {client.name}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Big box */}
            {caseStudy && (
              <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden bg-[#ECF0F6]">
                {/* Static background image — identical for every project.
                    No `priority`: this section is far below the fold and must
                    not compete with the page's LCP image. */}
                <Image
                  src={Images?.caseStudyBg}
                  alt=""
                  aria-hidden
                  fill
                  sizes="(max-width: 1024px) 100vw, 75vw"
                  className="pointer-events-none object-cover"
                />

                {/* Per-project image — bottom-right corner, cross-fades on change.
                    Desktop only (hidden via CSS => never fetched on mobile), and
                    only the active client + its neighbours are mounted/loaded. */}
                <div className="pointer-events-none absolute bottom-0 right-0 z-0 hidden h-[60%] w-[42%] lg:block">
                  {clients.map((client, i) => {
                    if (!client.caseStudy?.image) return null;
                    if (Math.abs(i - activeIndex) > 1) return null;
                    return (
                      <Image
                        key={client.id}
                        src={client.caseStudy.image}
                        alt=""
                        aria-hidden
                        fill
                        sizes="40vw"
                        className={`object-contain object-right-bottom transition-[opacity,transform] duration-700 ${
                          i === activeIndex
                            ? "translate-y-0 opacity-100"
                            : "translate-y-3 opacity-0"
                        }`}
                      />
                    );
                  })}
                </div>

                {/* Content */}
                <div
                  ref={contentBoxRef}
                  className="relative z-[1] flex w-full flex-col gap-4 overflow-y-auto p-5 xl:h-full xl:w-[80%] lg:justify-start lg:gap-6 lg:p-12 lg:[&>*]:shrink-0 lg:[&>:first-child]:mt-auto lg:[&>:last-child]:mb-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  <h3
                    className={`${REVEAL_ITEM_CLASS} clash-font text-2xl font-medium leading-[120%] text-[#0a0a0a] lg:text-[44px]`}
                  >
                    {caseStudy.title}
                  </h3>

                  {caseStudy.tags?.length > 0 && (
                    <div
                      className={`${REVEAL_ITEM_CLASS} flex w-fit max-w-full shrink-0 flex-wrap gap-2`}
                    >
                      {caseStudy.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="rounded-full bg-white px-4 py-2.5 text-[14px] font-medium text-[#00040B] lg:px-5 lg:text-[17px]"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {renderSection("challenge", caseStudy.challenge)}

                  {renderSection("solution", caseStudy.solution)}

                  {renderSection(
                    "impact",
                    <ul className="flex flex-col gap-2">
                      {caseStudy.impact.map((point) => (
                        <li key={point.id} className="flex items-start gap-2.5">
                          <CheckIcon />
                          <span>{point.text}</span>
                        </li>
                      ))}
                    </ul>,
                  )}

                  <div className={`${REVEAL_ITEM_CLASS} mt-1 w-fit`}>
                    <a
                      href={caseStudy.caseStudyHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <PrimaryBtn
                        text="View Case Study"
                        iconSrc={Icons?.chevron}
                        dark={false}
                      />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Desktop: vertical progress dots */}
            <div className="hidden shrink-0 flex-col items-center justify-center gap-4 lg:flex">
              {clients.map((client, i) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Show ${client.name}`}
                  aria-current={i === activeIndex}
                  className={`rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? "h-3 w-3 bg-white"
                      : "h-2.5 w-2.5 bg-white/30 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
